/**
 * DanaVoice - Language Context
 *
 * Provides DE/EN language toggle for the dashboard UI.
 * Stores preference in localStorage and provides t() translation function.
 */

import React, { createContext, useContext, useState, useCallback, type ReactNode } from 'react';
import { translations, type Language, type TranslationKey } from '../i18n/translations';

interface LanguageContextValue {
  language: Language;
  t: (key: TranslationKey, params?: Record<string, string | number>) => string;
  toggleLanguage: () => void;
}

const LanguageContext = createContext<LanguageContextValue | undefined>(undefined);

const STORAGE_KEY = 'danavoice_language';

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguage] = useState<Language>(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return (stored === 'de' || stored === 'en') ? stored : 'en';
    } catch {
      return 'en';
    }
  });

  const t = useCallback((key: TranslationKey, params?: Record<string, string | number>): string => {
    const entry = translations[key];
    if (!entry) {
      console.warn(`[i18n] Missing translation key: ${key}`);
      return key;
    }
    let text: string = entry[language];
    if (params) {
      for (const [param, value] of Object.entries(params)) {
        text = text.replace(new RegExp(`\\{\\{${param}\\}\\}`, 'g'), String(value));
      }
    }
    return text;
  }, [language]);

  const toggleLanguage = useCallback(() => {
    setLanguage(prev => {
      const next = prev === 'en' ? 'de' : 'en';
      try {
        localStorage.setItem(STORAGE_KEY, next);
      } catch {
        // localStorage might be unavailable
      }
      return next;
    });
  }, []);

  return (
    <LanguageContext.Provider value={{ language, t, toggleLanguage }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}
